import { useEffect, useState } from 'react'
import { ChevronLeft, ChevronRight, Save } from 'lucide-react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import type { BehaviorSetting, BehaviorSettings, BehaviorsState, FacilityBehaviors } from '../index.js'
import type { BehaviorsApi } from './behaviorsApi.js'
import { BehaviorSettingsTable } from './BehaviorSettingsTable.js'
import { CustomBehaviorsPanel } from './CustomBehaviorsPanel.js'
import { BehaviorsPreview } from './BehaviorsPreview.js'
import { anchorsDisagree, overridesFrom, sameSettings } from './behaviorDrafts.js'
import { Button, HelpIcon, Skeleton } from '../../core/index.js'
import { LoadFailure } from '../../core/index.js'

/** Which settings are on screen: the master, or one facility by id. */
export type BehaviorsScope = { kind: 'master' } | { kind: 'facility'; facilityId: string }

export const MASTER_SCOPE: BehaviorsScope = { kind: 'master' }

function settingsOf(facility: FacilityBehaviors): BehaviorSettings {
  return Object.fromEntries(facility.effective.map(({ key, enabled, weight, best, worst }) => [key, { enabled, weight, best, worst }]))
}

function overrideCounts(state: BehaviorsState): Record<string, number> {
  return Object.fromEntries(state.behaviors.map(({ key }) => [key, state.facilities.filter((facility) => key in facility.overrides).length]))
}

interface ScopePagerProps {
  state: BehaviorsState
  scope: BehaviorsScope
  /** Moving away would discard edits, so the pager is locked while there are any. */
  isLocked: boolean
  onScopeChange: (scope: BehaviorsScope) => void
}

/** Master on the left, then previous / pick / next through the facilities - all without leaving the view. */
function ScopePager({ state, scope, isLocked, onScopeChange }: ScopePagerProps) {
  const { facilities } = state
  const index = scope.kind === 'facility' ? facilities.findIndex((facility) => facility.facilityId === scope.facilityId) : -1
  const goTo = (position: number): void => {
    const facility = facilities[(position + facilities.length) % facilities.length]
    if (facility) {
      onScopeChange({ kind: 'facility', facilityId: facility.facilityId })
    }
  }
  const tabClass = (isActive: boolean): string =>
    `px-3 py-1.5 text-xs font-bold rounded border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
      isActive ? 'bg-marine-dk text-white border-marine-dk' : 'bg-white text-marine border-marine/30 hover:bg-marine-lt/40'
    }`
  const stepClass = 'w-8 h-8 rounded border border-marine/30 bg-white text-marine font-bold hover:bg-marine-lt/40 disabled:opacity-40 disabled:cursor-not-allowed'

  return (
    <div className="flex flex-wrap items-center gap-2">
      <button type="button" className={tabClass(scope.kind === 'master')} disabled={isLocked && scope.kind !== 'master'} onClick={() => onScopeChange(MASTER_SCOPE)}>
        Master{state.canEditMaster ? '' : ' (view only)'}
      </button>
      {facilities.length > 0 && (
        <div className={`flex items-center gap-1 rounded border p-0.5 ${scope.kind === 'facility' ? 'border-marine-dk bg-marine-lt/30' : 'border-transparent'}`}>
          <button type="button" aria-label="Previous facility" className={stepClass} disabled={isLocked || facilities.length < 2} onClick={() => goTo(index <= 0 ? facilities.length - 1 : index - 1)}>
            <ChevronLeft aria-hidden="true" className="mx-auto h-4 w-4" />
          </button>
          <select
            aria-label="Facility"
            disabled={isLocked}
            value={scope.kind === 'facility' ? scope.facilityId : ''}
            onChange={(event) => event.target.value !== '' && onScopeChange({ kind: 'facility', facilityId: event.target.value })}
            className="h-8 max-w-[16rem] rounded border border-marine/30 bg-white px-2 text-xs font-bold text-ink focus:outline-none focus:border-marine disabled:opacity-50"
          >
            {scope.kind === 'master' && <option value="">Choose a facility…</option>}
            {facilities.map((facility, position) => (
              <option key={facility.facilityId} value={facility.facilityId}>
                {facility.name} ({position + 1} of {facilities.length})
              </option>
            ))}
          </select>
          <button type="button" aria-label="Next facility" className={stepClass} disabled={isLocked || facilities.length < 2} onClick={() => goTo(index + 1)}>
            <ChevronRight aria-hidden="true" className="mx-auto h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  )
}

interface BehaviorsManagerProps {
  api: BehaviorsApi
  scope: BehaviorsScope
  onScopeChange: (scope: BehaviorsScope) => void
  /** True while something else on the page has unsaved edits that changing scope would discard. */
  hasOtherUnsavedChanges?: boolean
}

/**
 * Which behaviors are measured and how much each counts: once in a master
 * that every facility follows, with any facility able to set its own value
 * for any behavior. Edits are a draft until saved; a facility's draft is
 * saved as just its differences from the master, so the behaviors it did
 * not touch keep following the master's future changes.
 */
export function BehaviorsManager({ api, scope, onScopeChange, hasOtherUnsavedChanges = false }: BehaviorsManagerProps) {
  const queryClient = useQueryClient()
  const stateQuery = useQuery({ queryKey: api.queryKey, queryFn: () => api.load() })
  const state = stateQuery.data
  const facility = scope.kind === 'facility' ? state?.facilities.find((candidate) => candidate.facilityId === scope.facilityId) : undefined
  const saved: BehaviorSettings | null = !state ? null : scope.kind === 'master' ? state.master : facility ? settingsOf(facility) : null

  const [draft, setDraft] = useState<BehaviorSettings | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [customError, setCustomError] = useState<string | null>(null)
  const [justSaved, setJustSaved] = useState(false)

  // A different scope, or fresh data from the server, starts from what is saved.
  const scopeKey = scope.kind === 'master' ? 'master' : `facility:${scope.facilityId}`
  useEffect(() => {
    setDraft(null)
    setError(null)
  }, [scopeKey, state])
  // Separate from the reset above: a save replaces `state`, and its "Saved" note must survive that.
  useEffect(() => {
    setJustSaved(false)
  }, [scopeKey])

  if (stateQuery.isPending) {
    return <Skeleton lines={5} label="Loading behaviors" />
  }
  if (stateQuery.isError || !state) {
    return <LoadFailure message="Failed to load behaviors." onRetry={() => void stateQuery.refetch()} retrying={stateQuery.isFetching} />
  }

  const settings = draft ?? saved
  const isDirty = draft !== null && saved !== null && !sameSettings(draft, saved, state.behaviors)
  const canEdit = scope.kind === 'facility' || state.canEditMaster
  // The API refuses anchors that point the wrong way, so the save is held back until they are fixed.
  const clashing = settings === null ? [] : state.behaviors.filter((behavior) => settings[behavior.key] && anchorsDisagree(behavior, settings[behavior.key]!)).map((behavior) => behavior.label)

  /** Runs a change, puts the state it returns into the cache, and reports whether it worked. */
  async function apply(change: () => Promise<BehaviorsState>, onError: (message: string) => void): Promise<boolean> {
    setIsSaving(true)
    onError('')
    try {
      queryClient.setQueryData(api.queryKey, await change())
      return true
    } catch (err) {
      onError(err instanceof Error && err.message !== '' ? err.message : 'The change could not be saved')
      return false
    } finally {
      setIsSaving(false)
    }
  }
  const reportError = (message: string): void => setError(message === '' ? null : message)
  const reportCustomError = (message: string): void => setCustomError(message === '' ? null : message)

  async function save(): Promise<void> {
    if (!draft || !state) {
      return
    }
    const didSave =
      scope.kind === 'master'
        ? await apply(() => api.saveMaster(draft), reportError)
        : await apply(() => api.saveFacility(scope.facilityId, overridesFrom(draft, state.master, state.behaviors)), reportError)
    setJustSaved(didSave)
  }

  function change(key: string, setting: BehaviorSetting): void {
    setJustSaved(false)
    setDraft({ ...(settings ?? {}), [key]: setting })
  }

  return (
    <section className="bg-white border border-marine/15 rounded p-5">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h2 className="text-base font-black text-ink">Behaviors<HelpIcon topic="facilities.config-behaviors" className="ml-1" /></h2>
          <p className="text-[11px] text-ink-mid max-w-xl leading-relaxed">
            What is measured for every worker, and how much each behavior counts. The master applies to every facility; a facility can set its own
            value for any behavior. Everyone at a facility is measured the same way.
          </p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <ScopePager state={state} scope={scope} isLocked={isDirty || isSaving || hasOtherUnsavedChanges} onScopeChange={onScopeChange} />
          {hasOtherUnsavedChanges && <span className="text-[11px] text-ink-mid">Save the facility settings below before moving on.</span>}
        </div>
      </div>

      {settings === null ? (
        <div className="text-sm text-ink-mid">That facility could not be found.</div>
      ) : (
        <>
          <div className="text-xs font-extrabold text-ink mb-2">
            {scope.kind === 'master' ? 'Master configuration' : facility?.name}
            <span className="font-normal text-ink-mid">
              {scope.kind === 'master'
                ? ` · applies to ${state.facilities.length === 1 ? 'your facility' : `all ${state.facilities.length} facilities`} unless one sets its own`
                : ' · changes here apply to this facility only'}
            </span>
          </div>

          <BehaviorSettingsTable
            behaviors={state.behaviors}
            settings={settings}
            onChange={change}
            disabled={!canEdit || isSaving}
            {...(scope.kind === 'master' ? { overrideCounts: overrideCounts(state) } : { master: state.master })}
          />

          {canEdit && isDirty && draft && clashing.length === 0 && <BehaviorsPreview api={api} state={state} scope={scope} draft={draft} />}

          {canEdit ? (
            <div className="flex flex-wrap items-center gap-3 mt-4">
              <Button icon={Save} disabled={!isDirty || isSaving || clashing.length > 0} onClick={() => void save()}>
                {isSaving ? 'Saving…' : scope.kind === 'master' ? 'Save master' : 'Save for this facility'}
              </Button>
              {isDirty && (
                <button type="button" disabled={isSaving} onClick={() => setDraft(null)} className="text-xs font-bold text-ink-mid hover:text-ink">
                  Discard changes
                </button>
              )}
              {clashing.length > 0 ? (
                <span role="alert" className="text-xs text-coral font-bold">
                  {clashing.join(', ')}: the level that earns 100 must be {clashing.length === 1 ? 'on the right side of' : 'on the right side of'} the level that earns 0.
                </span>
              ) : isDirty ? (
                <span className="text-xs text-ink-mid">Unsaved changes. Save or discard before moving to another facility.</span>
              ) : (
                justSaved && <span className="text-xs text-green font-bold">Saved</span>
              )}
              {error && (
                <span role="alert" className="text-xs text-coral font-bold">
                  {error}
                </span>
              )}
            </div>
          ) : (
            <p className="text-[11px] text-ink-mid mt-3">Only an operator can change the master. You can set your own facilities’ values from their pages.</p>
          )}
        </>
      )}

      {scope.kind === 'master' && state.canEditMaster && (
        <div className="mt-6 pt-5 border-t border-marine/15">
          {/* Adding or removing a behavior reloads the master, which would throw away an unsaved draft of it. */}
          {isDirty ? (
            <p className="text-[11px] text-ink-mid">Save or discard the master changes above to add, edit or remove custom behaviors.</p>
          ) : (
          <CustomBehaviorsPanel
            custom={state.behaviors.filter((behavior) => behavior.kind === 'custom')}
            isSaving={isSaving}
            error={customError}
            onCreate={(input) => apply(() => api.createCustom(input), reportCustomError)}
            onUpdate={(key, changes) => apply(() => api.updateCustom(key, changes), reportCustomError)}
            onDelete={(key) => apply(() => api.deleteCustom(key), reportCustomError)}
          />
          )}
        </div>
      )}
    </section>
  )
}
